import type { Field, FilterOption } from '../search-panel-types';
import type { Category, Area } from '@ilteducation/content-ref-data-types/models/data-types';
import getCategoryTranslation from './get-category-translation';
import useLocale from './useLocale';
import { storeToRefs } from 'pinia';
import { useCategories, useContentRefData } from '@ilteducation/content-ref-data';
import { AggregateCount } from '../types';

const useFormatFilterOptions = () => {
  const { t } = useLocale();
  const contentRefDataStore = useContentRefData();
  const { byId } = storeToRefs(contentRefDataStore);
  const { byId: categoriesById } = useCategories();

  const categoryList = (counts: AggregateCount[], kind: string | undefined) => {
    const values: FilterOption[] = [];

    counts.forEach((item: AggregateCount) => {
      const category = categoriesById.value(item.key) as Category | undefined;
      // Only include categories associated with the current subscription - since we only store categories for current subscription
      if (category) {
        if (kind && category.kind !== kind) {
          return;
        }
        values.push({
          value: item.key,
          count: item.count,
          label: getCategoryTranslation(category),
        });
      }
    });
    return values;
  };

  const areaList = (counts: AggregateCount[]): FilterOption[] => {
    const values: FilterOption[] = [];

    counts.forEach((item: AggregateCount) => {
      const area = byId.value('areas', item.key) as Area;
      if (area) {
        values.push({
          value: item.key,
          count: item.count,
          label: area.name,
        });
      }
    });
    return values;
  };

  const languageLists = (counts: AggregateCount[], labelPrefix?: string): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce((values: FilterOption[], item: AggregateCount) => {
        if (!item.key) {
          return values;
        }

        return values.concat({
          value: item.key,
          count: item.count,
          label: labelPrefix ? (t(`${labelPrefix}.${item.key}`) as string) : (t(`${item.key}_long`) as string),
        });
      }, [] as FilterOption[])
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const audienceList = (counts: AggregateCount[]): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: t(`label_audience_${item.key}`) as string,
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  // Keywords are free text in admin, so most have no `material_keywords` translation.
  // vue-i18n echoes the key path on a miss, so fall back to the keyword itself.
  const keywordLabel = (keyword: string) => {
    const key = `material_keywords.${keyword}`;
    const label = t(key) as string;
    return label === key ? keyword.replace(/_/g, ' ') : label;
  };

  const keywordsList = (counts: AggregateCount[]): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: keywordLabel(item.key),
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const subscriptionList = (counts: AggregateCount[], labelPrefix?: string): FilterOption[] => {
    if (!counts.length) {
      return [];
    }

    return counts
      .reduce(
        (values: FilterOption[], item: AggregateCount) =>
          values.concat({
            value: item.key,
            count: item.count,
            label: labelPrefix
              ? (t(`${labelPrefix}.${item.key.replace('-', '_')}`) as string)
              : (t(
                  `label_${item.key.replace(/polyglutt-|polylino-/gi, 'subscription-').replace(/-/gi, '_')}`,
                ) as string),
          }),
        [] as FilterOption[],
      )
      .sort((a: FilterOption, b: FilterOption) => (a.label > b.label ? 1 : -1));
  };

  const transformToFilterOptions = (field: Field, counts: AggregateCount[]): FilterOption[] => {
    const key = field.aggregationKey;
    switch (true) {
      case key === 'properties.audiences':
        return audienceList(counts);
      case key === 'languages.text' ||
        key === 'languages.audio' ||
        key === 'languages.video' ||
        key === 'language' ||
        key === 'languages.secondaryLanguage':
        return languageLists(counts, field.item_label_prefix);
      case key === 'categories':
        return categoryList(counts, field.kind);
      case key === 'keywords':
        return keywordsList(counts);
      case key === 'areas':
        return areaList(counts);
      case key === 'accessRules.subscriptionType':
        return subscriptionList(counts, field.item_label_prefix);
      default:
        return counts.map((item) => ({ label: item.key, count: item.count, value: item.key }));
    }
  };

  return {
    transformToFilterOptions,
  };
};

export default useFormatFilterOptions;
